import React from "react";
import { motion } from "framer-motion";

// 剑玉扑克牌：54 张牌面 + 1 张卡背（卡背用于翻转时展示）
const cardImageModules = import.meta.glob("../剑玉扑克牌/*.jpg", {
  eager: true,
  import: "default",
}) as Record<string, string>;

const CARD_BACK_MARK = "卡背";

const cardBackSrc =
  Object.entries(cardImageModules).find(([path]) => path.includes(CARD_BACK_MARK))?.[1] ?? "";

// 从文件路径提取可读牌名，例如 "黑5-月面交错 拷贝.jpg" -> "黑5-月面交错"
function extractCardName(path: string): string {
  const base = path.slice(path.lastIndexOf("/") + 1);
  return base
    .replace(/\.jpg$/i, "")
    .replace(/\s*拷贝$/i, "")
    .trim();
}

// 扑克牌占卜含义：花色定主题，点数定具体含义
const CARD_MEANINGS: Record<string, string> = {
  // 黑桃：事业、挑战与思考
  "黑A": "新的挑战，需谨慎抉择",
  "黑2": "合作与抉择，避免冲动",
  "黑3": "沟通受阻，注意言行",
  "黑4": "工作压力，以稳为重",
  "黑5": "事业转折，迎难而上",
  "黑6": "稳步进展，坚持有回报",
  "黑7": "考验来临，保持冷静",
  "黑8": "努力耕耘，静待结果",
  "黑9": "目标渐近，勿功亏一篑",
  "黑10": "责任在肩，收获在望",
  "黑J": "年轻贵人，带来成长",
  "黑Q": "成熟助力，事业有靠",
  "黑K": "权威指引，方向渐明",
  // 红心：情感、人际与爱情
  "红A": "新的缘分，情感开始",
  "红2": "感情升温，彼此靠近",
  "红3": "沟通融洽，人缘渐好",
  "红4": "感情稳定，内心安宁",
  "红5": "情感波动，用心经营",
  "红6": "和谐相处，温暖相伴",
  "红7": "感情考验，坦诚以对",
  "红8": "关系进展，心意相通",
  "红9": "愿望达成，情意圆满",
  "红10": "幸福美满，情感丰收",
  "红J": "情缘喜讯，贵人相助",
  "红Q": "温柔助力，情感贵人",
  "红K": "稳重依靠，情感支柱",
  // 梅花：财富、机遇与事业
  "梅A": "新的机遇，财运开启",
  "梅2": "权衡取舍，谨慎投入",
  "梅3": "人脉助力，机遇浮现",
  "梅4": "稳定积累，财源渐稳",
  "梅5": "变动机遇，主动把握",
  "梅6": "顺风顺水，稳步进财",
  "梅7": "小有波折，坚持守成",
  "梅8": "努力回报，财运转好",
  "梅9": "接近成功，收获在望",
  "梅10": "圆满收获，财源广进",
  "梅J": "带来机遇的贵人",
  "梅Q": "助力事业的贵人",
  "梅K": "事业领路人，贵人扶持",
  // 方块：财富、行动与旅行
  "方A": "新计划启动，财运初现",
  "方2": "稳健理财，权衡取舍",
  "方3": "出行消息，财运流动",
  "方4": "财库稳固，积累有方",
  "方5": "波动中求稳，理性消费",
  "方6": "财运顺畅，小有进账",
  "方7": "谨慎理财，防微杜渐",
  "方8": "勤勉收获，财运渐旺",
  "方9": "愿望将成，财运可期",
  "方10": "丰盛收获，财源滚滚",
  "方J": "带来财运消息的贵人",
  "方Q": "精明助力，理财有方",
  "方K": "稳健财富，贵人扶持",
  // 王牌
  "大王": "命运转机，好运开启",
  "小王": "自由惊喜，把握当下",
};

// 从牌名解析花色与点数，返回对应占卜含义
function getCardMeaning(name: string): string {
  if (name.startsWith("大王")) return CARD_MEANINGS["大王"] ?? "";
  if (name.startsWith("小王")) return CARD_MEANINGS["小王"] ?? "";
  const suit = name.charAt(0);
  const rank = name.slice(1).match(/^(10|[2-9AJQK])/)?.[1] ?? "";
  return CARD_MEANINGS[`${suit}${rank}`] ?? "";
}

interface CardFace {
  name: string;
  url: string;
}

// 牌面按文件路径排序（路径稳定，不受构建哈希影响），保证每日选牌在多次构建间保持一致
const cardFaces: CardFace[] = Object.entries(cardImageModules)
  .filter(([path]) => !path.includes(CARD_BACK_MARK))
  .sort(([a], [b]) => a.localeCompare(b, "zh-Hans-CN"))
  .map(([path, url]) => ({ name: extractCardName(path), url }));

// 确定性洗牌：固定种子生成牌堆的固定顺序，保证 54 天一轮且期间不重复
function buildDailyCardOrder(length: number, seed: number): number[] {
  const order = Array.from({ length }, (_, i) => i);
  let s = seed >>> 0;
  for (let i = length - 1; i > 0; i -= 1) {
    s = (s * 1664525 + 1013904223) >>> 0;
    const j = s % (i + 1);
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
}

const DAILY_CARD_ORDER = buildDailyCardOrder(cardFaces.length, 20260101);

// 计算今日牌面：以本地日历日为准，54 天一个轮回，抽完重新开始
function getTodayCard(): CardFace | undefined {
  if (cardFaces.length === 0) return undefined;
  const anchor = Date.UTC(2026, 0, 1);
  const now = new Date();
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const dayNumber = Math.floor((todayUtc - anchor) / 86400000);
  const cycleIndex =
    ((dayNumber % cardFaces.length) + cardFaces.length) % cardFaces.length;
  return cardFaces[DAILY_CARD_ORDER[cycleIndex]];
}

const CARD_WIDTH = "min(46vw, 168px)";

// 单张牌面（正面或背面）
const CardFace: React.FC<{ src: string; transform: string; glow?: boolean }> = ({
  src,
  transform,
  glow = true,
}) => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      borderRadius: 16,
      overflow: "hidden",
      backfaceVisibility: "hidden",
      WebkitBackfaceVisibility: "hidden",
      transform,
      boxShadow: glow
        ? "0 0 24px rgba(255, 255, 255, 0.2), 0 20px 50px rgba(0, 0, 0, 0.4)"
        : "0 20px 50px rgba(0, 0, 0, 0.4)",
      background: "#1a1a1a",
    }}
  >
    {src ? (
      <img
        src={src}
        alt=""
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    ) : null}
  </div>
);

// 漂浮扑克牌：从底部弹出 + 绕 Y 轴翻转落定 + 持续漂浮，用于签到弹窗中间
export const FloatingPokerCard: React.FC<{
  scale?: number;
  initialY?: number | string;
  /** undefined = 挂载后自动从卡背翻到正面（默认）；false = 先展示卡背；true = 展示正面 */
  revealed?: boolean;
  /** 入场动画延迟（秒），用于让其它元素（如按钮）先于卡片出现 */
  entranceDelay?: number;
  /** 是否显示白色外发光（默认 true） */
  glow?: boolean;
}> = ({ scale = 1, initialY = 120, revealed, entranceDelay = 0, glow = true }) => {
  const todayCard = getTodayCard();
  const frontSrc = todayCard?.url ?? "";
  const backSrc = cardBackSrc;
  const cardWidth = scale === 1 ? CARD_WIDTH : `calc(${CARD_WIDTH} * ${scale})`;

  // 自动模式：720° 起多圈翻转落定；受控模式：从卡背（180°）起，revealed 为 true 时翻到正面
  const initialRotateY = revealed === undefined ? 720 : 180;
  const targetRotateY = revealed === false ? 180 : 0;

  return (
    <div className="flex flex-col items-center">
      <div style={{ perspective: 1200 }}>
      {/* 入场：从底部弹出 + 放大 + 渐显；用关键帧实现延迟，避免首帧闪现 */}
      <motion.div
        initial={false}
        animate={{
          y: entranceDelay > 0 ? [initialY, initialY, 0] : [initialY, 0],
          scale: entranceDelay > 0 ? [0.4, 0.4, 1] : [0.4, 1],
          opacity: entranceDelay > 0 ? [0, 0, 1] : [0, 1],
        }}
        transition={{
          y: {
            duration: entranceDelay + 1.2,
            ease: [0.22, 1, 0.36, 1],
            ...(entranceDelay > 0 ? { times: [0, entranceDelay / (entranceDelay + 1.2), 1] } : {}),
          },
          scale: {
            duration: entranceDelay + 1.2,
            ease: "backOut",
            ...(entranceDelay > 0 ? { times: [0, entranceDelay / (entranceDelay + 1.2), 1] } : {}),
          },
          opacity: {
            duration: entranceDelay + 0.5,
            ease: "easeOut",
            ...(entranceDelay > 0 ? { times: [0, entranceDelay / (entranceDelay + 0.5), 1] } : {}),
          },
        }}
        style={{
          transformStyle: "preserve-3d",
          width: cardWidth,
          aspectRatio: "5 / 7",
        }}
      >
        {/* 翻转：受控模式由卡背翻到正面，自动模式多圈翻转落定 */}
        <motion.div
          initial={{ rotateY: initialRotateY }}
          animate={{ rotateY: targetRotateY }}
          transition={{ rotateY: { duration: 1.5, ease: [0.22, 1, 0.36, 1] } }}
          style={{
            width: "100%",
            height: "100%",
            transformStyle: "preserve-3d",
          }}
        >
          {/* 漂浮感：落定后上下 + 轻微左右摆动，无限循环 */}
          <motion.div
            animate={{ y: [0, -12, 0], rotate: [0, 1.2, 0, -1.2, 0] }}
            transition={{
              duration: 3.2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.4 + entranceDelay,
            }}
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              transformStyle: "preserve-3d",
            }}
          >
            {revealed !== false ? <CardFace src={frontSrc} transform="rotateY(0deg)" glow={glow} /> : null}
            <CardFace src={backSrc} transform="rotateY(180deg)" glow={glow} />
          </motion.div>
        </motion.div>
      </motion.div>
      </div>
      {todayCard && revealed !== false ? (
        <>
          <p className="mt-4 font-pingfang text-sm font-semibold tracking-[0.06em] text-cyan-100/90">
            {todayCard.name}
          </p>
          <p className="mt-1 font-pingfang text-sm font-semibold tracking-[0.06em] text-cyan-100/70">
            {getCardMeaning(todayCard.name)}
          </p>
        </>
      ) : null}
    </div>
  );
};
